import { createContext, useContext, useMemo, useState } from "react";

import upperFirst from "lodash/upperFirst";

import { apiHelper } from "app/common/services/apiHelper";
import { useSearchAdvertisingDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useSearchAdvertisingDataAndParams";
import type { RuleWithField } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/types";
import DurationService from "app/services/DurationService";
import { requestParams } from "app/services/fetchService";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";
import {
    createCpcFilter,
    createVolumeFilter,
    getRangeFilterQueryParamValue,
} from "pro-features/components/filtersPanel/src/RangeFilterUtilityFunctions";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { FILTER_DEFAULT_SEPARATOR } from "app/pages/website-analysis/constants/constants";
import type { SearchEngine } from "app/pages/website-analysis/constants/searchEngines";
import allSearchEngines, { getSpecificSearchTypes } from "app/pages/website-analysis/constants/searchEngines";
import allSearchTypes from "app/pages/website-analysis/constants/searchTypes";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

import { useWebsiteKeywordsData } from "./hooks/useWebsiteKeywordsData";
import { PAGE_SIZE, SourceTypeMap, INITIAL_SORT } from "app/common/keyword-research/website-keywords/tableConsts";
import { useWebsiteKeywordsEndpoints } from "./websiteKeywordsEndpoints";
import type { IWebsiteKeywordsTransformDataReturn } from "app/common/keyword-research/website-keywords/websiteKeywordsPageTypes";
import { getSearchEnginesList } from "./WebsiteKeywordsPageUtillities2";

type ApiParamsValues = string | string[] | number | boolean;

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface IWebsiteKeywordsPage2Context {
    data: IWebsiteKeywordsTransformDataReturn;
    isLoading: boolean;
    isError: boolean;
    dataTimeout: boolean;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    sort: ISortProps;
    setSort: (sort: ISortProps) => void;
    excelUrl: string;
    apiParams: Record<string, ApiParamsValues>;
    requestBody: RuleWithField[];
    isCompare: boolean;
    searchEngineList: SearchEngine[];
}

const WebsiteKeywordsPage2Context = createContext<IWebsiteKeywordsPage2Context>(null);
export const useWebsiteKeywordsPage2Context = () =>
    useContext<IWebsiteKeywordsPage2Context>(WebsiteKeywordsPage2Context);

export const WebsiteKeywordsPage2ContextProvider = ({ children }: { children: React.ReactNode }) => {
    const params = useSearchAdvertisingDataAndParams();
    const endpoints = useWebsiteKeywordsEndpoints();
    const {
        key,
        isWWW,
        source,
        duration,
        IncludeBranded,
        IncludeNoneBranded,
        multiIncludeExcludeKeywords,
        multiIncludeExcludeUrl,
        family = "",
        trafficPreset,
        selectedPageTab,
        pageFilter,
        webSource,
        comparedDuration,
        country,
        Change,
        cpcFromValue,
        cpcToValue,
        volumeFromValue,
        volumeToValue,
    } = params;

    const [currentPage, setCurrentPage] = useState(1);
    const [sort, setSort] = useState<ISortProps>({
        sort: INITIAL_SORT.sort,
        asc: INITIAL_SORT.asc,
    });

    const isCompare = (pageFilter ? safeJsonParse(pageFilter, []) : (key as string).split(","))?.length > 1;

    const requestBody = useMemo(() => {
        const urls = safeJsonParse(multiIncludeExcludeUrl, []) as RuleWithField[];
        const keywords = safeJsonParse(multiIncludeExcludeKeywords, []) as RuleWithField[];
        return [...urls, ...keywords];
    }, [multiIncludeExcludeUrl, multiIncludeExcludeKeywords]);

    const searchEngineList = useMemo(() => {
        return selectedPageTab === EKeywordTabs[EKeywordTabs.Paid]
            ? getSpecificSearchTypes(["google", "yahoo"])
            : allSearchEngines;
    }, [selectedPageTab]);

    const searchEngines = getSearchEnginesList(family, searchEngineList, webSource);

    const searchTypes = useMemo(() => {
        return source?.length
            ? source.split(FILTER_DEFAULT_SEPARATOR).map((item) => allSearchTypes.find((st) => st.id === item).value)
            : undefined;
    }, [source]);

    const rangeFilter = getRangeFilterQueryParamValue([
        createVolumeFilter(volumeFromValue, volumeToValue, "volume"),
        createCpcFilter(cpcFromValue, cpcToValue, "cpc"),
    ]);

    const { compareFrom, compareTo } = DurationService.getDurationData(duration, comparedDuration).forAPI;

    const apiParams = useMemo(() => {
        return apiHelper.transformParamsForAPI({
            ...sort,
            country,
            Change,
            cpcFromValue,
            cpcToValue,
            volumeFromValue,
            volumeToValue,
            duration,
            keys: key,
            pageFilterJson: pageFilter,
            includeSubDomains: isWWW === "*",
            IncludeNoneBranded: IncludeNoneBranded === "true",
            IncludeBranded: IncludeBranded === "true",
            page: currentPage,
            pageSize: PAGE_SIZE,
            timeGranularity: GranularityNames.MONTHLY,
            searchEngines,
            Preset: trafficPreset,
            searchTypes,
            rangeFilter,
            webSource,
            sourceType: SourceTypeMap[selectedPageTab],
            compareFrom,
            compareTo,
        });
    }, [
        sort,
        key,
        pageFilter,
        isWWW,
        IncludeNoneBranded,
        IncludeBranded,
        currentPage,
        selectedPageTab,
        trafficPreset,
        searchEngines,
        searchTypes,
        rangeFilter,
        webSource,
        compareFrom,
        compareTo,
        duration,
        country,
        Change,
        cpcFromValue,
        cpcToValue,
        volumeFromValue,
        volumeToValue,
    ]) as Record<string, ApiParamsValues>;

    const paramsAsString = requestParams({ ...apiParams, sort: upperFirst(apiParams.sort as string) });

    const excelUrl = `${endpoints.excel}?${paramsAsString}`;

    const {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        data = { Data: [], TotalCount: 0 },
        isLoading,
        isError,
        dataTimeout,
    } = useWebsiteKeywordsData(paramsAsString, requestBody);

    const contextValue = useMemo<IWebsiteKeywordsPage2Context>(() => {
        return {
            data,
            isLoading,
            isError,
            dataTimeout,
            currentPage,
            setCurrentPage,
            sort,
            setSort,
            excelUrl,
            apiParams,
            requestBody,
            isCompare,
            searchEngineList,
        };
    }, [
        data,
        isLoading,
        isError,
        dataTimeout,
        currentPage,
        setCurrentPage,
        sort,
        setSort,
        excelUrl,
        apiParams,
        requestBody,
        isCompare,
        searchEngineList,
    ]);
    return <WebsiteKeywordsPage2Context.Provider value={contextValue}>{children}</WebsiteKeywordsPage2Context.Provider>;
};
